<!-- SEO landing page (target: "convert pdf to excel" ~22K/mo KD22 + "how to convert pdf (form) to excel" KD8-14 — pdf→data intent only). -->
<template>
  <SeoLanding
    kicker="PDF to Excel"
    h1="PDF to Excel: Convert Documents into Clean Spreadsheets"
    subtitle="Turn PDF invoices, receipts, statements, and forms into a ready-to-use Excel workbook — the fields already sorted onto labeled sheets, so you download an .xlsx and start working, not retyping."
    cta-label="Convert a PDF free"
    cta-to="/signup"
    :sections="sections"
    :faqs="faqs"
    closing-title="From PDF to spreadsheet in seconds"
    closing-subtitle="Upload a PDF, download a ready-to-use Excel workbook. Free for 5 pages a month — no credit card required."
    :related-links="relatedLinks"
  />
</template>

<script setup>
import SeoLanding from '@/components/marketing/SeoLanding.vue'
import { usePageSeo, faqJsonLd, articleJsonLd } from '@/composables/usePageSeo'

const CANONICAL = 'https://www.papersnap.io/pdf-to-excel'

const sections = [
  {
    h2: 'Why converting PDF to Excel usually means retyping',
    paragraphs: [
      'Open a PDF invoice or statement and there\'s no clean way to get it into Excel. Copy-paste turns neat rows into one jumbled column. Generic "PDF to Excel" converters dump the raw text and leave the layout for you to rebuild. And when the PDF is a scan or a phone photo, there\'s no text to copy at all — so most people end up retyping the numbers by hand.',
      'Papersnap reads the document the way a person would: it recognizes what kind of document it is, finds the fields that matter — invoice number, line items, totals — and lays them out as labeled columns. You download a ready-to-use Excel workbook (.xlsx), with the fields, line items, and each table on their own sheet — already structured. No untangling, no retyping.',
    ],
  },
  {
    h2: 'How to convert a PDF to Excel',
    bullets: [
      { label: '1. Upload your PDF', text: 'drag in an invoice, receipt, statement, report, or filled-in form — digital or scanned' },
      { label: '2. Papersnap extracts the fields', text: 'it detects the document type and pulls the data into labeled columns, not a raw text dump' },
      { label: '3. Download as Excel', text: 'export a ready-to-use .xlsx workbook — fields, line items, and tables each on their own sheet — or grab CSV / JSON instead' },
    ],
  },
  {
    h2: 'What you can pull into a spreadsheet',
    bullets: [
      { label: 'Invoices', text: 'invoice number, vendor, line items, tax, and total as tidy rows' },
      { label: 'Receipts', text: 'merchant, date, items, and total — ready for expense tracking' },
      { label: 'Bank & card statements', text: 'transactions and balances pulled into a spreadsheet-ready table' },
      { label: 'Reports & PDF forms', text: 'form fields and tables extracted with their structure intact' },
    ],
  },
  {
    h2: 'Scanned PDFs and photos work too',
    bullets: [
      { label: 'Born-digital PDFs', text: 'text is read directly for the fastest, most accurate result' },
      { label: 'Scanned PDFs & photos', text: 'OCR reads the image, so a scan converts to a spreadsheet just like a digital file' },
      { label: 'Labeled columns', text: 'you get named fields, not a raw text dump you have to clean up' },
      { label: 'Excel, CSV, or JSON', text: 'download a native .xlsx workbook, a CSV, or JSON for an API or integration' },
    ],
  },
]

const faqs = [
  { q: 'How do I convert a PDF to Excel?', a: 'Upload the PDF to Papersnap. It detects the document type, extracts the fields into labeled columns, and lets you download a ready-to-use Excel workbook (.xlsx) — or CSV / JSON — with no copy-paste or retyping.' },
  { q: 'Can I convert a PDF form to Excel?', a: 'Yes. Papersnap reads PDF forms and pulls the filled-in fields and tables into a structured spreadsheet, keeping their labels — rather than dumping the page as unformatted text.' },
  { q: 'Does it work on scanned PDFs, not just digital ones?', a: 'Yes. Digital PDFs are read directly; scanned PDFs and photos go through OCR. Both produce the same labeled, spreadsheet-ready output.' },
  { q: 'Do I get a real Excel file (.xlsx)?', a: 'Yes — Papersnap exports a native Excel workbook (.xlsx) with the fields, line items, and any tables on separate sheets, ready to sort, filter, and total. Prefer a flat file? CSV and JSON exports are available too.' },
  { q: 'Is the PDF to Excel converter free?', a: 'Yes — 5 pages a month free, no credit card. Paid plans from $9/month add higher volume and bulk export.' },
]

const relatedLinks = [
  { to: '/how-to-save-pdf-as-excel', label: 'How to save a PDF as Excel' },
  { to: '/how-to-convert-pdf-form-to-excel', label: 'PDF form to Excel' },
  { to: '/pdf-to-csv', label: 'PDF to CSV' },
]

usePageSeo({
  title: 'PDF to Excel — Convert Documents into Clean Spreadsheets',
  description: 'Convert PDF invoices, receipts, statements, and forms into a clean, labeled spreadsheet that opens in Excel. Digital and scanned PDFs supported; CSV and JSON export.',
  path: '/pdf-to-excel',
  jsonLd: [
    articleJsonLd({ headline: 'PDF to Excel: Convert Documents into Clean Spreadsheets', description: 'How to convert PDF invoices, receipts, statements, and forms into a clean labeled spreadsheet that opens in Excel.', url: CANONICAL, datePublished: '2026-07-10' }),
    faqJsonLd(faqs.map((f) => ({ question: f.q, answer: f.a }))),
  ],
})
</script>
